import { Model } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@/api/mock-server/server.js'
import { createErrorResponse } from '@/api/mock-server/utils.js'
import {
  getDateTimeTimestamp,
  getTodayDateTimeISO,
} from '@/utils/date/getter.js'

type SavedFilterData = {
  id: string
  name: string
  object_type: string
  filters: Record<string, unknown>
  departments: number[]
  created_by: number
  created_by_name: string
  is_own: boolean
  created_at: string
  updated_at: string
}

export const model = Model as ModelDefinition<SavedFilterData>

/**
 * Store saved filters in localStorage to persist across browser reloads.
 * Storage key: `pvc-mirage-saved-filters`
 */
const STORAGE_KEY = 'pvc-mirage-saved-filters'

function getFiltersFromStorage(): SavedFilterData[] {
  if (typeof window === 'undefined') {
    return []
  }

  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) {
    return []
  }

  try {
    return JSON.parse(stored)
  } catch {
    return []
  }
}

function setFiltersToStorage(filters: SavedFilterData[]): void {
  if (typeof window === 'undefined') {
    return
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
}

function generateId(): string {
  return String(Date.now())
}

export function routes(server: MockServer) {
  // GET /reports_v2/saved_filters - List saved filters
  server.get(
    '/reports_v2/saved_filters',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          status: 403,
          errors: [{ detail: 'User not authenticated' }],
        })
      }

      const params = request.queryParams
      const objectTypeFilter = params['filter[object_type]']

      let filters = getFiltersFromStorage()

      // Filter by object_type if specified
      if (objectTypeFilter) {
        filters = filters.filter((f) => f.object_type === objectTypeFilter)
      }

      // Sort by created_at descending (newest first)
      filters.sort(
        (a, b) =>
          getDateTimeTimestamp(b.created_at) -
          getDateTimeTimestamp(a.created_at),
      )

      // Mark which filters are owned by the current user
      const currentUserId = user.id ? parseInt(user.id) : 0
      const filtersWithOwnership = filters.map((f) => ({
        ...f,
        is_own: f.created_by === currentUserId,
      }))

      return {
        data: filtersWithOwnership.map((filter) => ({
          type: 'SavedFilter',
          id: filter.id,
          attributes: {
            name: filter.name,
            object_type: filter.object_type,
            filters: filter.filters,
            departments: filter.departments,
            created_by_name: filter.created_by_name,
            is_own: filter.is_own,
            created_at: filter.created_at,
            updated_at: filter.updated_at,
          },
        })),
        meta: {
          pagination: {
            count: filtersWithOwnership.length,
            page: 1,
            pages: 1,
          },
        },
      }
    },
  )

  // POST /reports_v2/saved_filters/ - Create a saved filter
  server.post(
    '/reports_v2/saved_filters',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          status: 403,
          errors: [{ detail: 'User not authenticated' }],
        })
      }

      const body = JSON.parse(request.requestBody)
      const attributes = body?.data?.attributes

      if (!attributes?.name || !attributes?.object_type) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Name and object_type are required' }],
        })
      }

      const now = getTodayDateTimeISO()
      const currentUserId = user.id ? parseInt(user.id) : 0
      const userName =
        `${user.attrs.first_name || ''} ${user.attrs.last_name || ''}`.trim()

      const newFilter: SavedFilterData = {
        id: generateId(),
        name: attributes.name,
        object_type: attributes.object_type,
        filters: attributes.filters || {},
        departments: attributes.departments || [],
        created_by: currentUserId,
        created_by_name: userName || 'Unknown User',
        is_own: true,
        created_at: now,
        updated_at: now,
      }

      const filters = getFiltersFromStorage()
      filters.push(newFilter)
      setFiltersToStorage(filters)

      return {
        data: {
          type: 'SavedFilter',
          id: newFilter.id,
          attributes: {
            name: newFilter.name,
            object_type: newFilter.object_type,
            filters: newFilter.filters,
            departments: newFilter.departments,
            created_by_name: newFilter.created_by_name,
            is_own: newFilter.is_own,
            created_at: newFilter.created_at,
            updated_at: newFilter.updated_at,
          },
        },
      }
    },
  )

  // GET /reports_v2/saved_filters/:id/ - Get a specific saved filter
  server.get(
    '/reports_v2/saved_filters/:id/',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          status: 403,
          errors: [{ detail: 'User not authenticated' }],
        })
      }

      const filterId = request.params.id
      const filters = getFiltersFromStorage()
      const filter = filters.find((f) => f.id === filterId)

      if (!filter) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Saved filter not found' }],
        })
      }

      const currentUserId = user.id ? parseInt(user.id) : 0

      return {
        data: {
          type: 'SavedFilter',
          id: filter.id,
          attributes: {
            name: filter.name,
            object_type: filter.object_type,
            filters: filter.filters,
            departments: filter.departments,
            created_by_name: filter.created_by_name,
            is_own: filter.created_by === currentUserId,
            created_at: filter.created_at,
            updated_at: filter.updated_at,
          },
        },
      }
    },
  )

  // PATCH /reports_v2/saved_filters/:id/ - Update a saved filter
  server.patch(
    '/reports_v2/saved_filters/:id/',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          status: 403,
          errors: [{ detail: 'User not authenticated' }],
        })
      }

      const filterId = request.params.id
      const filters = getFiltersFromStorage()
      const filterIndex = filters.findIndex((f) => f.id === filterId)

      if (filterIndex === -1) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Saved filter not found' }],
        })
      }

      const filter = filters[filterIndex]
      const currentUserId = user.id ? parseInt(user.id) : 0

      // Only owner can update (simplified - in real backend, admins can too)
      if (filter.created_by !== currentUserId) {
        return createErrorResponse({
          status: 403,
          errors: [
            { detail: 'You do not have permission to update this filter' },
          ],
        })
      }

      const body = JSON.parse(request.requestBody)
      const attributes = body?.data?.attributes || {}

      // Update allowed fields
      if (attributes.name !== undefined) {
        filter.name = attributes.name
      }
      if (attributes.filters !== undefined) {
        filter.filters = attributes.filters
      }
      if (attributes.departments !== undefined) {
        filter.departments = attributes.departments
      }

      filter.updated_at = getTodayDateTimeISO()

      filters[filterIndex] = filter
      setFiltersToStorage(filters)

      return {
        data: {
          type: 'SavedFilter',
          id: filter.id,
          attributes: {
            name: filter.name,
            object_type: filter.object_type,
            filters: filter.filters,
            departments: filter.departments,
            created_by_name: filter.created_by_name,
            is_own: true,
            created_at: filter.created_at,
            updated_at: filter.updated_at,
          },
        },
      }
    },
  )

  // DELETE /reports_v2/saved_filters/:id/ - Delete a saved filter
  server.delete(
    '/reports_v2/saved_filters/:id/',
    function (this: MirageServer, schema, request) {
      const user = schema.first('user')

      if (!user) {
        return createErrorResponse({
          status: 403,
          errors: [{ detail: 'User not authenticated' }],
        })
      }

      const filterId = request.params.id
      const filters = getFiltersFromStorage()
      const filterIndex = filters.findIndex((f) => f.id === filterId)

      if (filterIndex === -1) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Saved filter not found' }],
        })
      }

      const filter = filters[filterIndex]
      const currentUserId = user.id ? parseInt(user.id) : 0

      // Only owner can delete (simplified - in real backend, admins can too)
      if (filter.created_by !== currentUserId) {
        return createErrorResponse({
          status: 403,
          errors: [
            { detail: 'You do not have permission to delete this filter' },
          ],
        })
      }

      filters.splice(filterIndex, 1)
      setFiltersToStorage(filters)

      // Return 204 No Content
      return new Response(null, { status: 204 })
    },
  )
}

export function seeds(_server: MockServer) {
  // Seed some example saved filters for testing
  const existingFilters = getFiltersFromStorage()

  // Only seed if no filters exist
  if (existingFilters.length > 0) {
    return
  }

  const now = getTodayDateTimeISO()
  const sampleFilters: SavedFilterData[] = [
    {
      id: '1',
      name: 'Unpaid Invoices',
      object_type: 'invoice_v2',
      filters: {
        filters: {
          paymentStatus: ['unpaid'],
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [],
      },
      departments: [],
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      is_own: true,
      created_at: now,
      updated_at: now,
    },
    {
      id: '2',
      name: 'This Month',
      object_type: 'invoice_v2',
      filters: {
        filters: {
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [{ id: 'invoice_date', desc: true }],
      },
      departments: [],
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      is_own: true,
      created_at: now,
      updated_at: now,
    },
    {
      id: '3',
      name: 'This Month',
      object_type: 'invoice_v2',
      filters: {
        filters: {
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [{ id: 'invoice_date', desc: true }],
      },
      departments: [],
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      is_own: true,
      created_at: now,
      updated_at: now,
    },
    {
      id: '4',
      name: 'This Month',
      object_type: 'invoice_v2',
      filters: {
        filters: {
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [{ id: 'invoice_date', desc: true }],
      },
      departments: [],
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      is_own: true,
      created_at: now,
      updated_at: now,
    },
    {
      id: '5',
      name: 'This Month This Month. This Month This Month',
      object_type: 'invoice_v2',
      filters: {
        filters: {
          invoiceStatus: ['finalized'],
        },
        columnVisibility: {},
        sorting: [{ id: 'invoice_date', desc: true }],
      },
      departments: [],
      created_by: 1,
      created_by_name: 'Ada Lovelace',
      is_own: true,
      created_at: now,
      updated_at: now,
    },
  ]

  setFiltersToStorage(sampleFilters)
}
